import React, { useState } from "react";
import { MapPin, Clock, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { sendContact, waLink, EMAIL, MAPS_URL } from "../lib/api";
import { WhatsAppIcon } from "../components/WhatsAppIcon";
import { useSEO } from "../lib/seo";
import { metaTrack } from "../analytics/metaPixel";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useSEO({
    title: "Contact Us — R V Ilkal Sarees, Ilkal Karnataka",
    description: "Contact R V Ilkal Sarees for authentic Ilkal saree orders, support and bulk enquiries. Call, email or WhatsApp — Ilkal, Karnataka 587125.",
    canonicalPath: "/contact",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) return toast.error("Please add your name and message");
    setSending(true);
    try {
      await sendContact({ ...form, email: form.email || undefined, phone: form.phone || undefined });
      metaTrack("Contact", { content_name: "Contact form" });
      toast.success("Message sent. We will get back to you shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16" data-testid="contact-page">
      <p className="uppercase tracking-[0.25em] text-xs font-semibold text-gold">We reply fast</p>
      <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-charcoal mt-2">Contact Us</h1>

      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 mt-12">
        <div className="space-y-8">
          <a
            href={waLink("Namaste! I have a question.")}
            target="_blank"
            rel="noreferrer"
            data-testid="contact-whatsapp-btn"
            className="flex items-center gap-4 border border-gold/30 p-6 hover:border-gold transition-colors group"
          >
            <WhatsAppIcon className="w-7 h-7 text-[#128C4A]" />
            <span>
              <span className="block font-heading text-lg text-charcoal group-hover:text-maroon">WhatsApp — fastest</span>
              <span className="block text-sm text-charcoal/60 mt-0.5">Questions, orders, photos of sarees — chat with our team directly.</span>
            </span>
          </a>
          <div className="flex items-start gap-4 border border-gold/30 p-6" data-testid="contact-address">
            <MapPin className="w-7 h-7 text-maroon shrink-0" strokeWidth={1.25} />
            <span>
              <span className="block font-heading text-lg text-charcoal">R V Ilkal Sarees</span>
              <span className="block text-sm text-charcoal/60 mt-0.5 leading-relaxed">
                #4065/A Ward No.5, Koulpeth, Near Yamanoorappan Katti,<br />
                Ilkal 587125, Dist: Bagalkot, Karnataka
              </span>
              <a href={MAPS_URL} target="_blank" rel="noreferrer" data-testid="contact-map-link" className="inline-block text-xs tracking-[0.12em] uppercase text-gold-dark hover:text-maroon mt-2 underline underline-offset-4">
                View on Google Maps
              </a>
            </span>
          </div>
          <a href="tel:+919449909269" className="flex items-center gap-4 border border-gold/30 p-6 hover:border-gold transition-colors group" data-testid="contact-phone-card">
            <Phone className="w-7 h-7 text-maroon" strokeWidth={1.25} />
            <span>
              <span className="block font-heading text-lg text-charcoal group-hover:text-maroon">+91 94499 09269</span>
              <span className="block text-sm text-charcoal/60 mt-0.5">Call us for orders, tracking and support.</span>
            </span>
          </a>
          <a href={`mailto:${EMAIL}`} className="flex items-center gap-4 border border-gold/30 p-6 hover:border-gold transition-colors group" data-testid="contact-email-card">
            <Mail className="w-7 h-7 text-gold" strokeWidth={1.25} />
            <span>
              <span className="block font-heading text-lg text-charcoal group-hover:text-maroon">{EMAIL}</span>
              <span className="block text-sm text-charcoal/60 mt-0.5">Write to us anytime — we reply within a day.</span>
            </span>
          </a>
          <div className="flex items-center gap-4 border border-gold/30 p-6" data-testid="contact-hours">
            <Clock className="w-7 h-7 text-gold" strokeWidth={1.25} />
            <span>
              <span className="block font-heading text-lg text-charcoal">Support Hours</span>
              <span className="block text-sm text-charcoal/60 mt-0.5">Monday to Saturday, 10 AM – 7 PM IST</span>
            </span>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Input value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name" className="bg-ivory border-gold/40 rounded-sm h-12 focus-visible:ring-gold" />
            <Input value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" data-testid="contact-phone" className="bg-ivory border-gold/40 rounded-sm h-12 focus-visible:ring-gold" />
          </div>
          <Input value={form.email} onChange={set("email")} placeholder="Email (optional)" data-testid="contact-email" className="bg-ivory border-gold/40 rounded-sm h-12 focus-visible:ring-gold" />
          <Textarea value={form.message} onChange={set("message")} placeholder="How can we help?" rows={6} data-testid="contact-message" className="bg-ivory border-gold/40 rounded-sm focus-visible:ring-gold" />
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-btn"
            className="bg-maroon text-ivory text-xs tracking-[0.18em] uppercase px-10 py-4 hover:bg-maroon-dark transition-colors disabled:opacity-60"
          >
            {sending ? "Sending…" : "Send Message"}
          </button>
        </form>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-16" data-testid="policies-section">
        <div className="border border-gold/30 p-8" data-testid="shipping-policy">
          <h2 className="font-heading text-xl text-charcoal">Shipping Policy</h2>
          <ul className="mt-4 space-y-2.5 text-sm text-charcoal/70 list-disc pl-5">
            <li>Shipping within India is free for orders above ₹1,999.</li>
            <li>Orders below ₹2,000 carry a flat shipping fee of ₹150.</li>
            <li>We ship from Ilkal City, Karnataka.</li>
            <li>Domestic orders ship via Delhivery, Bluedart, India Post and FedEx.</li>
            <li>Tracking information is emailed to you as soon as your order ships.</li>
          </ul>
        </div>
        <div className="border border-gold/30 p-8" data-testid="returns-policy">
          <h2 className="font-heading text-xl text-charcoal">Returns</h2>
          <p className="mt-4 text-sm text-charcoal/70 leading-relaxed">
            If you want to return a product, mail us at our official email{" "}
            <a href={`mailto:${EMAIL}`} className="text-maroon underline underline-offset-2">{EMAIL}</a>{" "}
            or call our customer care on{" "}
            <a href="tel:+919449909269" className="text-maroon underline underline-offset-2">+91 94499 09269</a>.
          </p>
        </div>
      </div>
    </main>
  );
}
